/**
 * Transfer protocol receiver.
 *
 * - Accepts the `offer`, sanitizes the names (cross-OS) and replies with `accept`
 *   including `resume[i]` = bytes it ALREADY has of each file (0 in a
 *   new transfer; >0 when resuming by reusing sinks from a previous session).
 * - Writes each binary chunk at the offset of its header (idempotent).
 * - At `fileEnd`, it verifies the SHA-256 of the assembled file and confirms with
 *   `fileAck{ok}`. If it does not match, `ok=false` and it WAITS: the sender resends the
 *   whole file (positional writes → consistent rewrite) and another
 *   `fileEnd` arrives to re-verify. Only the sender's `abort` (it
 *   gave up) or a `bye` with unverified files is fatal.
 * - Emits per-chunk progress and `onText` for clipboard messages.
 */

import { dataChannelAdapter, type TransferChannel } from "./channel";
import { decodeChunk } from "./framing";
import { dedupeFilename, sanitizeFilename, sanitizeRelativePath } from "./filename";
import { sha256Hex } from "./hash";
import { decodeControl, encodeControl, type FileMeta } from "./protocol";
import type { FileSink } from "./source";
import type { TransferProgress } from "./sender";

export interface ReceivedFile {
  index: number;
  name: string;
  size: number;
  mime: string;
  sink: FileSink;
}

export interface ReceiveOptions {
  /**
   * Creates (or recovers) the sink of each offered file. The `name` already comes
   * sanitized. To resume, returns a sink that already contains the previous bytes
   * (its `bytesReceived()` marks the resume point).
   */
  createSink: (meta: { index: number; name: string; size: number; mime: string }) => FileSink;
  onProgress?: (progress: TransferProgress) => void;
  onFileDone?: (file: ReceivedFile) => void;
  onText?: (body: string) => void;
  onDone?: (files: readonly ReceivedFile[]) => void;
  onError?: (error: Error) => void;
}

/** Starts listening on `channel` for an incoming transfer. */
export function receiveFiles(
  channel: TransferChannel,
  options: ReceiveOptions,
): { cancel: () => void } {
  let files: ReceivedFile[] = [];
  let totalBytes = 0;
  const usedNames = new Set<string>();
  const verified = new Set<number>();
  let closed = false;

  function priorTotal(index: number): number {
    let sum = 0;
    for (let k = 0; k < index && k < files.length; k += 1) {
      sum += files[k]?.size ?? 0;
    }
    return sum;
  }

  channel.onClose(() => {
    if (!closed) {
      closed = true;
      // A mid-way close is not an error if the sender sent `bye` before; if not,
      // the caller can resume by reusing the sinks (bytesReceived).
    }
  });

  channel.onMessage((data) => {
    if (data instanceof ArrayBuffer) {
      handleChunk(data);
      return;
    }
    const message = decodeControl(data);
    if (!message) {
      return;
    }
    switch (message.t) {
      case "offer": {
        handleOffer(message.files);
        break;
      }
      case "fileStart": {
        // The resume offset also travels in each chunk's header;
        // this message only marks the start of a file for traces/UX.
        break;
      }
      case "fileEnd": {
        void handleFileEnd(message.i, message.sha256);
        break;
      }
      case "text": {
        options.onText?.(message.body);
        break;
      }
      case "abort": {
        options.onError?.(new Error(`aborted:${message.reason}`));
        break;
      }
      case "bye": {
        // Ordered close: it is only "done" if EVERYTHING offered was verified.
        // A correct sender does not send `bye` before; this protects against broken/
        // malicious senders so as not to accept an incomplete receive as good.
        if (files.every((f) => verified.has(f.index))) {
          options.onDone?.(files);
        } else {
          options.onError?.(new Error("incomplete"));
        }
        break;
      }
      default: {
        break;
      }
    }
  });

  function handleOffer(metas: FileMeta[]): void {
    files = metas.map((meta, index) => {
      // A name with separators is a relative path (folder send): it is
      // sanitized segment by segment (traversal impossible); otherwise, a loose name.
      const clean = /[/\\]/.test(meta.name)
        ? sanitizeRelativePath(meta.name)
        : sanitizeFilename(meta.name);
      const name = dedupeFilename(clean, usedNames);
      usedNames.add(name);
      const sink = options.createSink({ index, name, size: meta.size, mime: meta.mime });
      return { index, name, size: meta.size, mime: meta.mime, sink };
    });
    totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    const resume = files.map((f) => Math.min(f.sink.bytesReceived(), f.size));
    channel.send(encodeControl({ t: "accept", v: 1, resume }));
  }

  function handleChunk(data: ArrayBuffer): void {
    const frame = decodeChunk(data);
    if (!frame) {
      // Failed CRC or corrupt header: it is discarded. The final SHA-256 of the
      // file will fail → fileAck ok=false → the sender retries/aborts.
      return;
    }
    const file = files[frame.fileIndex];
    if (!file) {
      return;
    }
    void (async () => {
      await file.sink.write(frame.offset, frame.payload);
      options.onProgress?.({
        fileIndex: file.index,
        fileName: file.name,
        fileSentBytes: file.sink.bytesReceived(),
        fileTotalBytes: file.size,
        totalSentBytes: priorTotal(file.index) + file.sink.bytesReceived(),
        totalBytes,
      });
    })();
  }

  async function handleFileEnd(index: number, expectedSha: string): Promise<void> {
    const file = files[index];
    if (!file) {
      return;
    }
    if (verified.has(index)) {
      // Duplicate `fileEnd` of an already verified file: re-confirms and does not
      // close the sink again nor re-emit onFileDone.
      channel.send(encodeControl({ t: "fileAck", v: 1, i: index, ok: true }));
      return;
    }
    let ok = true;
    const { readAll } = file.sink;
    if (readAll) {
      try {
        const assembled = await readAll.call(file.sink);
        const actual = await sha256Hex(assembled);
        ok = actual === expectedSha;
      } catch {
        ok = false;
      }
    }
    channel.send(encodeControl({ t: "fileAck", v: 1, i: index, ok }));
    if (ok) {
      verified.add(index);
      await Promise.resolve(file.sink.close?.());
      options.onFileDone?.(file);
    }
    // With ok=false NO error is emitted: the sender will resend the whole file
    // (new fileEnd → re-verification) or send `abort` if it exhausts retries.
  }

  return {
    cancel() {
      closed = true;
      channel.close();
    },
  };
}

/** Sugar: receives over an `RTCDataChannel`. */
export function receiveFilesOverDataChannel(
  dc: RTCDataChannel,
  options: ReceiveOptions,
): { cancel: () => void } {
  return receiveFiles(dataChannelAdapter(dc), options);
}
